import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { aiBudgetMessages } from '../aiBudgetMessages';
import { reelProgressMessages } from '../studio/aiReel/reelProgressMessages';
import { StudioActionError } from '../studio/StudioActionError';
import { studioMessages } from '../studio/studioMessages';
import { aiReelStudioParams } from '../studioRoute';
import { SerializedReel } from '../types';
import { WorkRow } from './studioWorkModel';
import { studioWorkMessages } from './studioWorkMessages';
import { useStudioWorkActions } from './useStudioWorkActions';
import { workRowText } from './workRowText';

interface WorkRowFailedActionProps {
  accountId: number;
  row: WorkRow;
  catalogItem: CatalogItem;
  /** The tab of the release page that holds the file: the Kit tab for a keeper, else Posts. */
  releaseTab: 'kit' | 'posts';
  className?: string;
  testID?: string;
}

/**
 * The button of a failed kit asset or reel (the Work page, the Making strip of Posts): the button
 * of the old Work row. A kit asset is made again (its credits came back when it failed); a reel
 * opens its page in the Studio; a master that cannot be used opens the catalog; a spent Free AI
 * budget opens the upgrade. A failed try shows its sentence next to the button.
 */
const WorkRowFailedActionComponent: React.FC<WorkRowFailedActionProps> = (props) => {
  const { accountId, row, catalogItem, releaseTab, className, testID } = props;
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgrade = useUpgradePrompt();
  const openAsset = useCallback(
    (asset: SerializedKitAsset) =>
      router.push(SMLRoutes.promoteRelease, { catalogItemUuid: catalogItem.uuid, tab: releaseTab, asset: asset.uuid }),
    [catalogItem.uuid, releaseTab, router]
  );
  const openReel = useCallback(
    (reel: SerializedReel) =>
      router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ catalogItemUuid: catalogItem.uuid, reel: reel.uuid })),
    [catalogItem.uuid, router]
  );
  const actions = useStudioWorkActions({
    accountId,
    item: catalogItem,
    onOpenAsset: openAsset,
    onRetryReel: openReel,
    onChangeReelSong: openReel,
  });
  const text = useMemo(() => workRowText(intl, row, catalogItem), [catalogItem, intl, row]);

  const { retryAsset, retryReel, changeReelSong, openCatalog, retrying, retryFeedback } = actions;
  const retry = useCallback(() => {
    if (text.failAction === 'upgrade') {
      openUpgrade(SMLUpgradeFeature.aiPriority);
    } else if (text.failAction === 'openCatalog') {
      openCatalog();
    } else if (row.kind === 'asset') {
      retryAsset(row.asset);
    } else if (text.failAction === 'changeSong') {
      changeReelSong(row.reel);
    } else {
      retryReel(row.reel);
    }
  }, [changeReelSong, openCatalog, openUpgrade, retryAsset, retryReel, row, text]);

  if (row.state !== 'failed' || text.failAction === 'none') {
    return null;
  }
  const label = {
    tryAgain: intl.formatMessage(studioMessages.tryAgain),
    openCatalog: intl.formatMessage(studioWorkMessages.openCatalog),
    changeSong: intl.formatMessage(reelProgressMessages.changeSong),
    upgrade: intl.formatMessage(aiBudgetMessages.upgrade),
  }[text.failAction];
  const feedback =
    row.kind === 'asset' && retryFeedback !== null && retryFeedback.assetUuid === row.asset.uuid
      ? retryFeedback.feedback
      : null;
  const isRetrying = row.kind === 'asset' && retrying === row.asset.uuid;

  return (
    <>
      <button type="button" className={className} disabled={isRetrying} onClick={retry} data-testid={testID}>
        {label}
      </button>
      <StudioActionError feedback={feedback} />
    </>
  );
};

export const WorkRowFailedAction = TypedMemo(WorkRowFailedActionComponent);
